import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ImageUp, ScanFace, Plus, Smartphone } from 'lucide-react';
import { PERMISSIONS, FaceShape, type CustomerFaceAssessmentSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { formatDateTime } from '@/utils/format';
import { useMeasurementSettingsQuery } from '@/modules/measurements/api/measurements.api';
import { useFaceAssessmentsQuery } from '../api/face.api';
import { FaceShapeGlyph } from './FaceShapeGlyph';
import { FaceCameraDialog } from './FaceCameraDialog';
import { FacePhoneHandoverDialog } from './FacePhoneHandoverDialog';
import { SetFaceShapeDialog } from './SetFaceShapeDialog';
import { VoidFaceAssessmentDialog } from './VoidFaceAssessmentDialog';

/**
 * A customer's face-shape history, newest first.
 *
 * History rather than a current value, for the same reason the measurements card is: an order
 * advised in March was advised on what was known in March. The newest confirmed row is the current
 * shape, and it is derived here and on the server rather than stored anywhere.
 */
export function FaceShapeHistory({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [isSetting, setSetting] = useState(false);
  const [voiding, setVoiding] = useState<CustomerFaceAssessmentSummary | null>(null);
  const [isAnalysing, setAnalysing] = useState(false);
  const [isHandingOver, setHandingOver] = useState(false);
  const [isReadingPhoto, setReadingPhoto] = useState(false);

  const { data, isPending, isError, error } = useFaceAssessmentsQuery(customerId);
  const canAssess = can(PERMISSIONS.FACE_ASSESS);
  // The camera is released separately from the manual path, which is always available.
  const settings = useMeasurementSettingsQuery();
  const cameraAvailable =
    canAssess && settings.data !== undefined && settings.data.faceAnalysisMode !== 'OFF';

  return (
    <Card>
      <CardHeader
        title={t('face.title')}
        subtitle={t('face.subtitle')}
        action={
          canAssess ? (
            <div className="flex flex-wrap gap-2">
              {cameraAvailable ? (
                <>
                  <Button size="sm" variant="secondary" onClick={() => setAnalysing(true)}>
                    <Camera className="size-4" aria-hidden />
                    {t('face.capture.open')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setHandingOver(true)}>
                    <Smartphone className="size-4" aria-hidden />
                    {t('face.phone.open')}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setReadingPhoto(true)}>
                    <ImageUp className="size-4" aria-hidden />
                    {t('face.photo.open')}
                  </Button>
                </>
              ) : null}
              <Button size="sm" onClick={() => setSetting(true)}>
                <Plus className="size-4" aria-hidden />
                {t('face.set.open')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {isError ? (
        <div className="p-4 pt-0">
          <Alert tone="error">{apiErrorMessage(error) ?? t('face.errors.load')}</Alert>
        </div>
      ) : isPending ? (
        <LoadingState />
      ) : (data ?? []).length === 0 ? (
        <EmptyState icon={ScanFace} title={t('face.empty')} description={t('face.emptyHint')} />
      ) : (
        <ul className="divide-line divide-y">
          {(data ?? []).map((assessment) => (
            <AssessmentRow
              key={assessment.id}
              assessment={assessment}
              canAssess={canAssess}
              onVoid={() => setVoiding(assessment)}
            />
          ))}
        </ul>
      )}

      {canAssess ? (
        <>
          <SetFaceShapeDialog
            customerId={customerId}
            isOpen={isSetting}
            onClose={() => setSetting(false)}
          />
          <VoidFaceAssessmentDialog
            assessment={voiding}
            isOpen={voiding !== null}
            onClose={() => setVoiding(null)}
          />
          {cameraAvailable ? (
            <>
              <FaceCameraDialog
                customerId={customerId}
                isOpen={isAnalysing}
                onClose={() => setAnalysing(false)}
                onEnterManually={() => {
                  setAnalysing(false);
                  setSetting(true);
                }}
              />
              <FacePhoneHandoverDialog
                customerId={customerId}
                isOpen={isHandingOver}
                onClose={() => setHandingOver(false)}
              />
              <FaceCameraDialog
                input="PHOTO"
                customerId={customerId}
                isOpen={isReadingPhoto}
                onClose={() => setReadingPhoto(false)}
                onEnterManually={() => {
                  setReadingPhoto(false);
                  setSetting(true);
                }}
              />
            </>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

function AssessmentRow({
  assessment,
  canAssess,
  onVoid,
}: {
  assessment: CustomerFaceAssessmentSummary;
  canAssess: boolean;
  onVoid: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  const shape = assessment.confirmedShape;
  const isVoided = assessment.status === 'VOIDED';
  const isCurrent = !isVoided && !assessment.isSuperseded && shape !== null;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 p-4">
      <span className={`shrink-0 ${isVoided ? 'text-faint' : 'text-brand-600'}`}>
        <FaceShapeGlyph shape={shape ?? FaceShape.UNKNOWN} className="h-10 w-8" />
      </span>

      <span className="flex min-w-40 flex-1 flex-col">
        <span
          className={`text-sm font-medium ${isVoided ? 'text-muted line-through' : 'text-ink'}`}
        >
          {shape ? t(`face.shapes.${shape}`) : t('face.shapes.UNKNOWN')}
        </span>

        {/* The two real shapes behind a mixed face, never hidden behind the word. */}
        {shape === FaceShape.MIXED &&
        assessment.confirmedComponentA &&
        assessment.confirmedComponentB ? (
          <span className="text-muted text-xs">
            {t('face.components', {
              a: t(`face.shapes.${assessment.confirmedComponentA}`),
              b: t(`face.shapes.${assessment.confirmedComponentB}`),
            })}
          </span>
        ) : null}

        <span className="text-muted text-xs">
          {assessment.captureInput
            ? t(`face.inputs.${assessment.captureInput}`)
            : t(`face.sources.${assessment.source}`)}
          {assessment.confirmedByName
            ? ` · ${t('face.confirmedBy', { name: assessment.confirmedByName })}`
            : ''}
        </span>

        <span className="text-muted text-xs" dir="ltr">
          {formatDateTime(assessment.confirmedAt ?? assessment.assessedAt, locale)}
        </span>

        {isVoided && assessment.voidReason ? (
          <span className="text-muted text-xs">
            {t('face.voidedReason', { reason: assessment.voidReason })}
          </span>
        ) : null}
      </span>

      {isCurrent ? <Badge tone="success">{t('face.current')}</Badge> : null}
      {isVoided ? <Badge tone="neutral">{t('face.voided')}</Badge> : null}

      {canAssess && !isVoided ? (
        <Button size="sm" variant="ghost" onClick={onVoid}>
          {t('face.void')}
        </Button>
      ) : null}
    </li>
  );
}
